
/*
 * -- Extenal Imports --
 */
import { Box, Typography, styled } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { DateTime } from 'luxon';

/*
 * -- Internal Imports --
 */
import Image from '../components/common/Image';

// -- Home page images
import BGImage from '../assets/images/feral-scan/AusOutback-crop.jpg';
import BGImage01 from '../assets/images/feral-scan/background/Background-01.jpg';
import BGImage02 from '../assets/images/feral-scan/background/Background-02.jpg';
import BGImage03 from '../assets/images/feral-scan/background/Background-03.jpg';
import BGImage04 from '../assets/images/feral-scan/background/Background-04.jpg';
import BGImage05 from '../assets/images/feral-scan/background/Background-05.jpg';
import BGImage06 from '../assets/images/feral-scan/background/Background-06.jpg';
import BGImage07 from '../assets/images/feral-scan/background/Background-07.jpg';
import BGImage08 from '../assets/images/feral-scan/background/Background-08.jpg';
import BGImage09 from '../assets/images/feral-scan/background/Background-09.jpg';
import BGImage10 from '../assets/images/feral-scan/background/Background-10.jpg';
import BGImage11 from '../assets/images/feral-scan/background/Background-11.jpg';
import FeralScanLogo from '../assets/images/feral-scan/feralscan-logo-bright-crop.png';
import HomePageButton from '../components/buttons/HomePageButton';

// -- Sponsors

// -- Page elements

// -- Button logos
import { grey } from '@mui/material/colors';
import DeerIcon from '../assets/images/feral-scan/icons/home/Deer.png';
import DeerHoverIcon from '../assets/images/feral-scan/icons/home/DeerHover.png';
import FeralCatIcon from '../assets/images/feral-scan/icons/home/FeralCat.png';
import FeralCatHoverIcon from '../assets/images/feral-scan/icons/home/FeralCatHover.png';
import FeralFishIcon from '../assets/images/feral-scan/icons/home/Fish.png';
import FeralFishHoverIcon from '../assets/images/feral-scan/icons/home/FishHover.png';
import FoxIcon from '../assets/images/feral-scan/icons/home/Fox.png';
import FoxHoverIcon from '../assets/images/feral-scan/icons/home/FoxHover.png';
import FeralGoatIcon from '../assets/images/feral-scan/icons/home/Goat.png';
import FeralGoatHoverIcon from '../assets/images/feral-scan/icons/home/GoatHover.png';
import MouseIcon from '../assets/images/feral-scan/icons/home/Mouse.png';
import MouseHoverIcon from '../assets/images/feral-scan/icons/home/MouseHover.png';
import MynaIcon from '../assets/images/feral-scan/icons/home/Myna.png';
import MynaHoverIcon from '../assets/images/feral-scan/icons/home/MynaHover.png';
import NewPestsIcon from '../assets/images/feral-scan/icons/home/NewPests.png';
import NewPestsHoverIcon from '../assets/images/feral-scan/icons/home/NewPestsHover.png';
import OtherIcon from '../assets/images/feral-scan/icons/home/OtherPests.png';
import OtherHoverIcon from '../assets/images/feral-scan/icons/home/OtherPestsHover.png';
import FeralPigIcon from '../assets/images/feral-scan/icons/home/Pig.png';
import FeralPigHoverIcon from '../assets/images/feral-scan/icons/home/PigHover.png';
import RabbitIcon from '../assets/images/feral-scan/icons/home/Rabbit.png';
import RabbitHoverIcon from '../assets/images/feral-scan/icons/home/RabbitHover.png';
import StarlingIcon from '../assets/images/feral-scan/icons/home/Starling.png';
import StarlingHoverIcon from '../assets/images/feral-scan/icons/home/StarlingHover.png';
import ToadIcon from '../assets/images/feral-scan/icons/home/Toad.png';
import ToadHoverIcon from '../assets/images/feral-scan/icons/home/ToadHover.png';
import WildDogIcon from '../assets/images/feral-scan/icons/home/WildDog.png';
import WildDogHoverIcon from '../assets/images/feral-scan/icons/home/WildDogHover.png';
import { SpeciesTypes } from '../constants/species/Species';
import { useSearchParams } from 'react-router-dom';


/*
 * -- Components -- 
 */

interface BackgroundImageProps {
    bgImage: string;
}

interface BackgroundImageBlurProps {
    bgImageBlur: string;
}

const BackgroundImage = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgImage',
})<BackgroundImageProps>(({ bgImage }) => ({
    display: 'block',
    position: 'absolute',
    top: '20px',
    left: 0,
    width: '100%',
    height: '100%',
    minHeight: '900px',
    backgroundImage: `url("${bgImage}")`,
    backgroundPosition: 'center top',
    backgroundRepeat: 'no-repeat',
    WebkitBackgroundSize: 'cover',
    MozBackgroundSize: 'cover',
    OBackgroundSize: 'cover',
    backgroundSize: 'cover',
    zIndex: 1,
    transition: 'background-image 0.5s ease-in-out',

    "@media (min-width:800px)": {
        top: "0px"
    }
}));

const BackgroundImageBlurTop = styled(Box)(() => ({
    position: 'absolute',
    width: '100%',
    height: '30%',
    top: '-20px',
    left: 0,
    backgroundImage: 'linear-gradient(to top, rgba(255, 255, 255, 0) 0%,#1D2F48 90%)',
    zIndex: 3,

    "@media (min-width: 800px)": {
        top: "0px"
    }
}));

const BackgroundImageBlurBottom = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'bgImageBlur',
})<BackgroundImageBlurProps>(({ bgImageBlur }) => ({
    position: 'absolute',
    width: '100%',
    height: '35%',
    bottom: 0,
    left: 0,
    backgroundImage: `linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ${bgImageBlur} 70%)`,
    zIndex: 3,
    transition: 'background-image 0.5s ease-in-out',
}));

const ErrorPageHeader = styled(Box)(() => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: '0 auto 90px auto',
    padding: '18px 30px',
    zIndex: 8,

    width: '100%',
    minHeight: '215px',
    backgroundColor: `${grey[700]}99`
}));

const ErrorPageButtons = styled(Box)(() => ({
    padding: '10px 0',
    position: 'relative',
    zIndex: 8
}));

const ErrorPageButtonsWrapper = styled(Box)(() => ({
    '@media (min-width:400px)': {
        paddingLeft: '22px',
        paddingRight: '22px'
    },
    '@media (min-width:800px)': {
        width: '700px',
        height: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'auto'
    },
    '@media (min-width:1300px)': {
        width: '1080px',
        height: 'auto',
        marginLeft: 'auto',
        marginRight: 'auto',
        overflow: 'auto'
    }
}));

const ErrorPageButtonsTable = styled(Box)(() => ({
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    zIndex: 5,

    '@media (min-width: 500px)': {
        paddingTop: '8px'
    },
    '@media (min-width: 800px)': {
        padding: '24px 0'
    }
}));

const images = [
    BGImage,
    BGImage01,
    BGImage02,
    BGImage03,
    BGImage04,
    BGImage05,
    BGImage06,
    BGImage07,
    BGImage08,
    BGImage09,
    BGImage10,
    BGImage11
]; 

const blurs = [
    '#E5763E',
    '#915F3B',
    '#604A3C',
    '#838819',
    '#4D3A2D',
    '#B5912F',
    '#B56F31',
    '#C98732',
    '#DA7C0B',
    '#C1951E',
    '#B49B33',
    '#C98732'
];

const ErrorPage: FC = () => {
    const [searchParams] = useSearchParams();

    const errorMessage = useMemo(() => {
        const error = searchParams.get('error');
        if (error) {
            return error;
        }

        return "An ERROR has occurred or the page you have requested does not exist."
    }, []);

    const dayOfYear = useMemo(() => {
        return DateTime.now().ordinal;
    }, []);

    const TEN_SECONDS = 10_000;
    const [bgIndex, setBgIndex] = useState<number>(DateTime.now().ordinal % images.length);

    useEffect(() => {
        if (!searchParams.get('galleryMode')) {
            setBgIndex(dayOfYear % images.length);
        }
        else if (searchParams.get('galleryMode')) {
            let interval: NodeJS.Timeout;
    
            const start = () => {
                interval = setInterval(() => {
                    setBgIndex((prev) => (prev + 1) % images.length);
                }, TEN_SECONDS);
            };
            const stop = () => clearInterval(interval);
            document.addEventListener('visibilitychange', () => {
                document.hidden ? stop() : start();
            });
    
            start();
    
            return stop;
        }
    }, []);

    const homePageButtons = useMemo(() => {
        return [
            <HomePageButton
                key={`home-page-button-wild-dog`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            WildDog
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.WILDDOG.path}
                img={WildDogIcon}
                imgHover={WildDogHoverIcon}
            />,
            <HomePageButton
                key={`home-page-button-deer`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Deer
                        </Typography>
                        Scan
                    </Typography>}
                nav={SpeciesTypes.DEER.path}
                img={DeerIcon}
                imgHover={DeerHoverIcon}
            />,
            <HomePageButton 
                key={`home-page-button-rabbit`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Rabbit
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.RABBIT.path}
                img={RabbitIcon}
                imgHover={RabbitHoverIcon}
            />,
            <HomePageButton
            key={`home-page-button-feral-cat`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            FeralCat
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALCAT.path}
                img={FeralCatIcon}
                imgHover={FeralCatHoverIcon}
            />,
            <HomePageButton
                key={`home-page-button-fox`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Fox
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.FOX.path}
                img={FoxIcon}
                imgHover={FoxHoverIcon}
            />,
            <HomePageButton
                key={`home-page-button-feral-pig`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            FeralPig
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALPIG.path}
                img={FeralPigIcon}
                imgHover={FeralPigHoverIcon}
            />,
            <HomePageButton
                key={`home-page-button-myna`} 
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Myna
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.MYNA.path}
                img={MynaIcon}
                imgHover={MynaHoverIcon}
            />,
            <HomePageButton
            key={`home-page-button-toad`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Toad
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.TOAD.path}
                img={ToadIcon}
                imgHover={ToadHoverIcon}
            />,
            <HomePageButton
                key={`home-page-button-mouse`} 
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Mouse
                        </Typography>
                        Alert
                    </Typography>
                }
                nav={SpeciesTypes.MOUSE.path}
                img={MouseIcon}
                imgHover={MouseHoverIcon}
            />,
            <HomePageButton
                key={`home-page-button-feral-goat`} 
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            FeralGoat
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALGOAT.path}
                img={FeralGoatIcon}
                imgHover={FeralGoatHoverIcon}
            />,
            <HomePageButton 
                key={`home-page-button-feral-fish`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            FeralFish
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.FERALFISH.path}
                img={FeralFishIcon}
                imgHover={FeralFishHoverIcon}
            />,
            <HomePageButton 
                key={`home-page-button-starling`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Starling
                        </Typography>
                        Scan
                    </Typography>
                }
                nav={SpeciesTypes.STARLING.path}
                img={StarlingIcon}
                imgHover={StarlingHoverIcon}
            />,
            <HomePageButton 
                key={`home-page-button-other-pests`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            Other
                        </Typography>
                        Pests
                    </Typography>
                }
                nav={SpeciesTypes.OTHERPESTS.path}
                img={OtherIcon}
                imgHover={OtherHoverIcon}
            />,
            <HomePageButton 
                key={`home-page-button-new-pests`}
                label={
                    <Typography paragraph sx={{ textShadow: '2px 2px 6px rgba(0, 0, 0, 1)' }}>
                        <Typography variant="h6" display='inline'>
                            New
                        </Typography>
                        Pests
                    </Typography>
                }
                nav={SpeciesTypes.NEWPESTS.path}
                img={NewPestsIcon}
                imgHover={NewPestsHoverIcon}
            />
        ]
    }, []);

    return (
        <Box>
            {/* -- Background Image -- */}
            <BackgroundImage bgImage={images[bgIndex]}>
                <BackgroundImageBlurTop />
                <BackgroundImageBlurBottom bgImageBlur={blurs[bgIndex]} />
            </BackgroundImage>

            {/* -- Header -- */}
            <ErrorPageHeader>
                <Image src={FeralScanLogo} width='200px'  />
                <Typography color="#eee" fontWeight={600} variant="h1" mt='50px'>{errorMessage}</Typography>
            </ErrorPageHeader>
            
            {/* -- Body -- */}
            <Box bgcolor="#E5763E">
                <ErrorPageButtons>
                    <ErrorPageButtonsWrapper>
                        <ErrorPageButtonsTable>
                            {
                                homePageButtons.map(x => x)
                            }
                        </ErrorPageButtonsTable>
                    </ErrorPageButtonsWrapper>
                </ErrorPageButtons>
            </Box>
        </Box>
    )
}

export default ErrorPage